import type { ReactiveController, ReactiveControllerHost } from 'lit';
import { History, type HistoryOptions } from '../history.js';
import type { Command } from '../interfaces/command.interface.js';

export interface HistoryControllerOptions<TState> extends HistoryOptions {
  /** The state object commands will be applied to / reverted against. */
  state: TState;
}

/**
 * Binds a `History<TState>` to a Lit host. Calls `host.requestUpdate()` whenever
 * the undo/redo stacks change, so UI like disabled undo/redo buttons stays in sync.
 *
 * Usage:
 *   const history = new HistoryController(this, { state: this.boardState });
 *   history.push(new AddObjectCommand(id));
 *   history.undo();
 */
export class HistoryController<TState = unknown> implements ReactiveController {
  readonly history: History<TState>;
  private readonly host: ReactiveControllerHost;
  private readonly state: TState;
  private unsubscribe?: () => void;

  constructor(host: ReactiveControllerHost, options: HistoryControllerOptions<TState>) {
    this.host = host;
    this.state = options.state;
    this.history = new History<TState>({ limit: options.limit });
    host.addController(this);
  }

  hostConnected(): void {
    this.unsubscribe = this.history.onChange(() => this.host.requestUpdate());
  }

  hostDisconnected(): void {
    this.unsubscribe?.();
    this.unsubscribe = undefined;
  }

  get canUndo(): boolean { return this.history.canUndo; }
  get canRedo(): boolean { return this.history.canRedo; }

  push(command: Command<TState>): void {
    this.history.push(command, this.state);
  }

  undo(): boolean {
    return this.history.undo(this.state);
  }

  redo(): boolean {
    return this.history.redo(this.state);
  }

  clear(): void {
    this.history.clear();
  }
}
